import { inject, Injectable } from '@angular/core';

import * as fromRoot from '@core/redux/index';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  CreateAction,
  CreateFailureAction,
  CreateSuccessAction,
  DeleteAction,
  DeleteSuccessAction,
  SaveAction,
  SaveFailureAction,
  SaveSuccessAction,
  SearchFailureAction,
  SearchSuccessAction,
  TagActionTypes,
} from './tag.actions';
import { TagModel } from '../../models/tag.model';
import { TagService } from '../../services/tag.service';

@Injectable()
export class TagEffects {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly tagService = inject(TagService);
  private readonly actions$ = inject(Actions);

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActionTypes.SearchAction),
      switchMap(() =>
        this.tagService.findAll().pipe(
          map(tag => new SearchSuccessAction(tag)),
          catchError(errors => of(new SearchFailureAction(errors)))
        )
      )
    )
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActionTypes.SaveAction),
      map((action: SaveAction) => action.payload),
      switchMap((model: TagModel) =>
        this.tagService.save(model).pipe(
          map(tag => new SaveSuccessAction(tag)),
          catchError(errors => of(new SaveFailureAction(errors)))
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActionTypes.CreateAction),
      map((action: CreateAction) => action.payload),
      switchMap((model: TagModel) =>
        this.tagService.create(model).pipe(
          map(tag => new CreateSuccessAction(tag)),
          catchError(errors => of(new CreateFailureAction(errors)))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagActionTypes.DeleteAction),
      map((action: DeleteAction) => action.payload),
      switchMap((model: TagModel) =>
        this.tagService.delete(model).pipe(
          map(_ => new DeleteSuccessAction(model.id)),
          catchError(errors => of(new SaveFailureAction(errors)))
        )
      )
    )
  );
}
